import { useState, useEffect, useMemo, useCallback } from "react"
import { getDownloadUrl } from "./api"
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "@ciq/shared/components/paginatedTable"

const defaultDependencies = {
  fetchFn: typeof window !== "undefined" ? window.fetch : null,
  apiClient: { getDownloadUrl }
}

/**
 * Validate and normalize a page size value.
 * Returns the value if valid, or DEFAULT_PAGE_SIZE if invalid.
 * @param {number} size - Page size to validate
 * @returns {number} Valid page size
 */
function validatePageSize(size) {
  if (PAGE_SIZE_OPTIONS.includes(size)) {
    return size
  }
  return DEFAULT_PAGE_SIZE
}

/**
 * Filter zones by search query (multi-field: zone_name, rooms).
 * @param {Array} zones - All zones
 * @param {string} query - Search query
 * @returns {Array} Filtered zones
 */
function filterZones(zones, query) {
  if (!query) return zones
  const q = query.toLowerCase()
  return zones.filter(z =>
    z.zone_id?.toLowerCase().includes(q) ||
    z.zone_name?.toLowerCase().includes(q) ||
    z.rooms?.some(r => r.toLowerCase().includes(q))
  )
}

/**
 * Sort zones by zone_name (case-insensitive).
 * @param {Array} zones - Zones to sort
 * @returns {Array} Sorted zones
 */
function sortZones(zones) {
  return [...zones].sort((a, b) => {
    const nameA = (a.zone_name || "").toLowerCase()
    const nameB = (b.zone_name || "").toLowerCase()
    return nameA.localeCompare(nameB)
  })
}

/**
 * @typedef {Object} ZonePreviewData
 * @property {string} zone_id - Unique zone identifier
 * @property {string} zone_name - Human-readable zone name
 * @property {string} zone_type - "building", "floor", or "default"
 * @property {number} square_footage - Summed from rooms
 * @property {number} capacity - Summed from rooms
 * @property {string[]} rooms - Array of room numbers
 */

/**
 * @typedef {Object} ZonePreviewDataResult
 * @property {Array<ZonePreviewData>} data - Current page of zones (filtered + paginated)
 * @property {number} totalCount - Total zones (filtered count if searching)
 * @property {number} page - Current page (0-indexed)
 * @property {number} pageSize - Current page size
 * @property {string} searchQuery - Current search query
 * @property {boolean} isLoading - Loading state
 * @property {string|null} error - Error message if failed
 * @property {function(number): void} setPage - Change page
 * @property {function(number): void} setPageSize - Change page size
 * @property {function(string): void} setSearchQuery - Update search
 * @property {function(): void} retry - Retry failed fetch
 */

/**
 * Hook for fetching and managing zone preview data.
 *
 * @param {string} schoolId - School identifier
 * @param {string|null} jobId - Job identifier (null to skip fetch)
 * @param {Object} [options] - Optional configuration and dependencies
 * @param {number} [options.initialPageSize] - Initial page size (must be one of PAGE_SIZE_OPTIONS, defaults to DEFAULT_PAGE_SIZE)
 * @param {function} [options.fetchFn] - Fetch function (defaults to window.fetch)
 * @param {Object} [options.apiClient] - API client with getDownloadUrl method
 * @returns {ZonePreviewDataResult}
 */
export function useZonePreviewData(schoolId, jobId, options = {}) {
  const { initialPageSize, fetchFn, apiClient } = { ...defaultDependencies, ...options }

  const [allZones, setAllZones] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(() => validatePageSize(initialPageSize ?? DEFAULT_PAGE_SIZE))
  const [searchQuery, setSearchQuery] = useState("")
  const [retryCount, setRetryCount] = useState(0)

  // Fetch data when jobId changes or on retry
  useEffect(() => {
    if (!jobId) {
      setAllZones([])
      setIsLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    const MAX_RETRIES = 2

    async function fetchData(attempt = 0) {
      if (cancelled) return

      setIsLoading(true)
      setError(null)

      try {
        // Step 1: Get presigned URL from backend
        const { download_url: downloadUrl } = await apiClient.getDownloadUrl(schoolId, jobId, "output")

        if (cancelled) return

        // Step 2: Fetch data from S3
        let response
        try {
          response = await fetchFn(downloadUrl)
        } catch (networkError) {
          // Network error - retry
          if (attempt < MAX_RETRIES) {
            const delay = Math.pow(2, attempt) * 500 // Exponential backoff
            await new Promise(resolve => setTimeout(resolve, delay))
            return fetchData(attempt + 1)
          }
          throw networkError
        }

        if (cancelled) return

        // Step 3: Handle S3 response
        if (!response.ok) {
          // S3 error - don't retry
          console.warn("S3 error fetching zone data:", response.status)
          setError("Zone data file is unavailable. Please try processing again.")
          setIsLoading(false)
          return
        }

        // Step 4: Parse JSON
        let data
        try {
          data = await response.json()
        } catch (parseError) {
          if (cancelled) return
          console.warn("Failed to parse zone data:", parseError)
          setError("Zone data file is corrupted. Please try processing again.")
          setIsLoading(false)
          return
        }

        if (cancelled) return

        setAllZones(data.zones || [])
        setIsLoading(false)
      } catch (err) {
        if (cancelled) return
        console.warn("Failed to fetch zone data:", err)
        setError("Unable to load zone data. Please check your connection and try again.")
        setIsLoading(false)
      }
    }

    fetchData()

    return () => {
      cancelled = true
    }
  }, [jobId, schoolId, apiClient, fetchFn, retryCount])

  // Reset page when search or pageSize changes
  const handleSetSearchQuery = useCallback((query) => {
    setSearchQuery(query || "")
    setPage(0)
  }, [])

  const handleSetPageSize = useCallback((newSize) => {
    setPageSize(validatePageSize(newSize))
    setPage(0)
  }, [])

  const retry = useCallback(() => {
    setRetryCount(c => c + 1)
  }, [])

  // Compute filtered, sorted, and paginated data
  const { data, totalCount } = useMemo(() => {
    const filtered = filterZones(allZones, searchQuery)
    const sorted = sortZones(filtered)
    const start = page * pageSize
    return {
      data: sorted.slice(start, start + pageSize),
      totalCount: sorted.length
    }
  }, [allZones, searchQuery, page, pageSize])

  return {
    data,
    totalCount,
    page,
    pageSize,
    searchQuery,
    isLoading,
    error,
    setPage,
    setPageSize: handleSetPageSize,
    setSearchQuery: handleSetSearchQuery,
    retry
  }
}
